import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type StatsDashboard } from "@/lib/api";

/**
 * Estadísticas del panel (Fase 3): qué marcas/modelos/empresas piden más
 * soporte, garantías, antigüedad de abiertos y fallas recurrentes. Todo
 * calculado server-side (/api/stats/dashboard); aquí solo se pinta con
 * barras CSS (sin librería de gráficas, bundle chico).
 *
 * Qué muestra cada tarjeta y qué es cada número (🛡, 📟) se explica en la
 * Ayuda de Estadísticas (`components/ayuda/vistas/estadisticas.tsx`), no en
 * tooltips: si cambias una tarjeta, cambia también su explicación allá.
 */
export default function EstadisticasPanel() {
  const [stats, setStats] = useState<StatsDashboard | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get<StatsDashboard>("/api/stats/dashboard")
      .then(setStats)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <p className="text-xs text-zinc-600">No pude cargar las estadísticas.</p>
    );
  }
  if (!stats) {
    return <p className="text-xs text-zinc-600">Cargando estadísticas…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <CardPosesion stats={stats} />
        <CardConteos
          titulo="Marcas con más soporte"
          filas={stats.por_marca.map((m) => ({
            etiqueta: m.nombre,
            casos: m.casos,
            garantias: m.garantias,
            equipos: m.equipos,
            href: `/casos?marca=${encodeURIComponent(m.nombre)}`,
          }))}
        />
        <CardConteos
          titulo="Modelos con más soporte"
          filas={stats.por_modelo.map((m) => ({
            etiqueta: `${m.marca ? `${m.marca} · ` : ""}${m.modelo}`,
            casos: m.casos,
            garantias: m.garantias,
            equipos: m.equipos,
            href: `/casos?${m.marca ? `marca=${encodeURIComponent(m.marca)}&` : ""}modelo=${encodeURIComponent(m.modelo)}`,
          }))}
        />
        <CardConteos
          titulo="Empresas que más soporte piden"
          filas={stats.por_cliente.map((c) => ({
            etiqueta: c.nombre,
            casos: c.casos,
            href: `/casos?cliente_id=${c.id}&cliente_nombre=${encodeURIComponent(c.nombre)}`,
          }))}
        />
        <CardConteos
          titulo="Casos por categoría"
          filas={stats.por_categoria.map((c) => ({
            etiqueta: c.categoria,
            casos: c.casos,
            href: `/casos?categoria=${encodeURIComponent(c.slug || "__sin__")}&categoria_nombre=${encodeURIComponent(c.categoria)}`,
          }))}
        />
        <CardConteos
          titulo="Fallas recurrentes por modelo"
          filas={stats.fallas_por_modelo.map((f) => ({
            etiqueta: `${f.modelo} — ${f.falla}`,
            casos: f.casos,
            equipos: f.equipos,
            href: `/casos?${f.marca ? `marca=${encodeURIComponent(f.marca)}&` : ""}modelo=${encodeURIComponent(f.modelo)}&tipo_detalle=${encodeURIComponent(f.falla)}`,
          }))}
        />
        <CardAging stats={stats} />
      </div>
    </div>
  );
}

const POSESION_ETIQUETA: Record<string, string> = {
  nosotros: "🛠 Con nosotros",
  cliente: "⏳ Con el cliente",
  proveedor: "📦 Con el proveedor",
};

function CardPosesion({ stats }: { stats: StatsDashboard }) {
  const filas = stats.por_posesion ?? [];
  const max = Math.max(1, ...filas.map((p) => p.casos));
  return (
    <div className="card space-y-2 md:col-span-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-400 w-fit">
        ¿Dónde está la pelota? (abiertos)
      </h3>
      <ul className="space-y-2">
        {filas.map((p) => (
          <li key={p.posesion} className="space-y-0.5">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <Link
                to={`/casos?posesion=${p.posesion}`}
                className="text-zinc-300 hover:text-accent-300 hover:underline"
              >
                {POSESION_ETIQUETA[p.posesion] ?? p.posesion}
              </Link>
              <span className="text-zinc-400 shrink-0">
                <strong className="text-zinc-200">{p.casos}</strong>{" "}
                caso{p.casos === 1 ? "" : "s"}
                {p.dias_promedio != null ? ` · ${p.dias_promedio} d prom` : ""}
              </span>
            </div>
            <div className="h-1.5 rounded bg-ink-800 overflow-hidden">
              <div
                className="h-full bg-accent-500/40"
                style={{ width: `${(p.casos / max) * 100}%` }}
              />
            </div>
            {p.mas_viejo && (
              <p className="text-[10px] text-zinc-500">
                más viejo:{" "}
                <Link
                  to={`/casos/${p.mas_viejo.folio}`}
                  className="font-mono hover:text-accent-300 hover:underline"
                >
                  {p.mas_viejo.folio}
                </Link>{" "}
                ({p.mas_viejo.cliente}) · {p.mas_viejo.dias} d en {p.mas_viejo.estado}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

type Fila = { etiqueta: string; casos: number; garantias?: number; equipos?: number; href?: string };

function CardConteos({
  titulo,
  filas,
}: {
  titulo: string;
  filas: Fila[];
}) {
  const max = Math.max(1, ...filas.map((f) => f.casos));
  return (
    <div className="card space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-400 w-fit">
        {titulo}
      </h3>
      {filas.length === 0 ? (
        <p className="text-xs text-zinc-600">
          Aún no hay datos — se llena conforme se capturen casos con su tipo
          (p. ej. categoría "Falla" + tipo de falla en Nuevo caso).
        </p>
      ) : (
        <ul className="space-y-1.5">
          {filas.map((f) => (
            <li key={f.etiqueta} className="space-y-0.5">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                {f.href ? (
                  <Link
                    to={f.href}
                    className="text-zinc-300 break-words min-w-0 hover:text-accent-300 hover:underline"
                  >
                    {f.etiqueta}
                  </Link>
                ) : (
                  <span className="text-zinc-300 break-words min-w-0">
                    {f.etiqueta}
                  </span>
                )}
                <span className="flex items-center gap-1.5 shrink-0 font-mono text-zinc-400">
                  {f.garantias != null && f.garantias > 0 && (
                    <span className="text-violet-300" aria-label={`${f.garantias} en garantía`}>
                      {f.garantias}🛡
                    </span>
                  )}
                  {f.equipos != null && (
                    <span
                      className="text-accent-300"
                      aria-label={`${f.equipos} equipo${f.equipos === 1 ? "" : "s"}`}
                    >
                      {f.equipos}📟
                    </span>
                  )}
                  <span aria-label={`${f.casos} caso${f.casos === 1 ? "" : "s"}`}>{f.casos}</span>
                </span>
              </div>
              <div className="h-1.5 rounded bg-ink-800 overflow-hidden" aria-hidden>
                <div
                  className="h-full bg-accent-500/60"
                  style={{ width: `${(f.casos / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CardAging({ stats }: { stats: StatsDashboard }) {
  const max = Math.max(1, ...stats.aging_distribucion.map((r) => r.casos));
  return (
    <div className="card space-y-2 md:col-span-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-400 w-fit">
        Casos abiertos por antigüedad
      </h3>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="shrink-0">
          <div className="text-2xl font-mono text-zinc-200">
            {stats.aging_promedio_dias ?? "—"}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">
            días promedio
          </div>
        </div>
        <div className="flex-1 grid grid-cols-5 gap-2 items-end">
          {stats.aging_distribucion.map((r) => (
            <div key={r.rango} className="space-y-0.5 text-center">
              <div className="text-xs font-mono text-zinc-300">{r.casos}</div>
              <div className="h-12 flex items-end rounded bg-ink-800 overflow-hidden" aria-hidden>
                <div
                  className="w-full bg-accent-500/60"
                  style={{ height: `${(r.casos / max) * 100}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-500">{r.rango} d</div>
            </div>
          ))}
        </div>
      </div>
      {stats.aging_top.length > 0 && (
        <div className="border-t border-ink-700 pt-2 space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-zinc-500">
            Los más viejos
          </p>
          <ul className="space-y-1">
            {stats.aging_top.map((c) => (
              <li
                key={c.folio}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
              >
                <Link
                  to={`/casos/${c.folio}`}
                  className="font-mono text-accent-400 hover:underline"
                >
                  {c.folio}
                </Link>
                <span className="text-zinc-400 break-words min-w-0">
                  {c.cliente}
                </span>
                <span className="ml-auto shrink-0 font-mono text-zinc-400">
                  {c.dias} d
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
